import React, { useState } from 'react';
import {Link} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiCheckCircle, FiArrowRight, FiBarChart, FiTarget, FiTrendingUp, FiUsers, FiHeart, FiRefreshCw} = FiIcons;

function Assessment() {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const sections = [
    {
      title: "Transparens och sårbarhet",
      icon: FiHeart,
      color: "from-pink-500 to-rose-500",
      questions: [
        { id: 'transparency_1', question: "Medarbetare känner sig bekväma med att säga 'jag vet inte' under möten", category: 'transparency' },
        { id: 'transparency_2', question: "Det är accepterat att be om förtydliganden när något är oklart", category: 'transparency' },
        { id: 'transparency_3', question: "Misstag ses som lärandetillfällen snarare än misslyckanden", category: 'transparency' },
        { id: 'transparency_4', question: "Medarbetare vågar ifrågasätta beslut och processer", category: 'transparency' },
        { id: 'transparency_5', question: "Ledare delar öppet sina egna osäkerheter och kunskapsluckor", category: 'transparency' }
      ]
    },
    {
      title: "Gemenskap och tillit",
      icon: FiUsers,
      color: "from-blue-500 to-indigo-500",
      questions: [
        { id: 'community_1', question: "Teammedlemmar hjälper varandra utan att bli ombedda", category: 'community' },
        { id: 'community_2', question: "Det är naturligt att be kollegor om hjälp", category: 'community' },
        { id: 'community_3', question: "Konstruktiv feedback ges och tas emot positivt", category: 'community' },
        { id: 'community_4', question: "Informella interaktioner och skratt är vanliga på arbetsplatsen", category: 'community' },
        { id: 'community_5', question: "Teamet firar både individuella och gemensamma framgångar", category: 'community' }
      ]
    },
    {
      title: "Balans och välmående",
      icon: FiTarget,
      color: "from-green-500 to-emerald-500",
      questions: [
        { id: 'balance_1', question: "Medarbetare stannar hemma när de är sjuka utan skulkänslor", category: 'balance' },
        { id: 'balance_2', question: "Det finns tydliga gränser mellan arbetstid och fritid", category: 'balance' },
        { id: 'balance_3', question: "Medarbetare kan säga nej till nya uppgifter när de är överbelastade", category: 'balance' },
        { id: 'balance_4', question: "Semester och ledighet uppmuntras och respekteras", category: 'balance' },
        { id: 'balance_5', question: "Det är okej att ha dåliga dagar och uttrycka det", category: 'balance' }
      ]
    },
    {
      title: "Autonomi och individualitet",
      icon: FiTrendingUp,
      color: "from-amber-500 to-orange-500",
      questions: [
        { id: 'autonomy_1', question: "Medarbetare kan välja hur de organiserar sin arbetsplats", category: 'autonomy' },
        { id: 'autonomy_2', question: "Flexibilitet finns gällande arbetstider och arbetssätt", category: 'autonomy' },
        { id: 'autonomy_3', question: "Individuella preferenser för kommunikation respekteras", category: 'autonomy' },
        { id: 'autonomy_4', question: "Medarbetare kan påverka sina arbetsprocesser och metoder", category: 'autonomy' },
        { id: 'autonomy_5', question: "Det finns utrymme för tysta arbetsdagar och djup koncentration", category: 'autonomy' }
      ]
    }
  ];

  const scaleLabels = [
    "Stämmer inte alls",
    "Stämmer delvis",
    "Neutral",
    "Stämmer mestadels",
    "Stämmer helt"
  ];

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: parseInt(value)
    }));
  };

  const calculateResults = () => {
    const categories = ['transparency', 'community', 'balance', 'autonomy'];
    const results = {};
    
    categories.forEach(category => {
      const categoryQuestions = sections.flatMap(section => 
        section.questions.filter(q => q.category === category)
      );
      const categoryAnswers = categoryQuestions.map(q => answers[q.id] || 0);
      const average = categoryAnswers.reduce((sum, val) => sum + val, 0) / categoryAnswers.length;
      const percentage = (average / 4) * 100;
      
      results[category] = {
        score: average,
        percentage: Math.round(percentage),
        level: percentage >= 80 ? 'Utmärkt' : 
               percentage >= 60 ? 'Bra' : 
               percentage >= 40 ? 'Utvecklingsområde' : 
               'Kritiskt förbättringsområde'
      };
    });
    
    const overallScore = Object.values(results).reduce((sum, cat) => sum + cat.percentage, 0) / 4;
    results.overall = Math.round(overallScore);
    
    return results;
  };

  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      // Scroll to top when changing sections
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      // For the last section, show results and scroll to top
      setShowResults(true);
      // Scroll to top after showing results
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      // Scroll to top when changing sections
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  };

  const restartAssessment = () => {
    setCurrentSection(0);
    setAnswers({});
    setShowResults(false);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  const isCurrentSectionComplete = () => {
    const currentQuestions = sections[currentSection].questions;
    return currentQuestions.every(q => answers[q.id] !== undefined);
  };

  // Scroll to top when results are shown
  React.useEffect(() => {
    if (showResults) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [showResults]);

  if (showResults) {
    const results = calculateResults();
    return (
      <div className="py-12">
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                Dina <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">resultat</span>
              </h1>
              <div className="bg-white rounded-3xl p-8 shadow-lg mb-8">
                <div className="text-center mb-8">
                  <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600 mb-2">
                    {results.overall}%
                  </div>
                  <p className="text-xl text-gray-600">Övergripande kulturmognad</p>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(results).filter(([key]) => key !== 'overall').map(([category, data], index) => {
                    const section = sections.find(s => s.questions[0]?.category === category);
                    return (
                      <div key={category} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center mb-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center mr-3`}>
                            <SafeIcon icon={section.icon} className="text-white text-sm" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{section.title}</h3>
                            <p className="text-sm text-gray-600">{data.level}</p>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className={`h-3 rounded-full bg-gradient-to-r ${section.color}`}
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{data.percentage}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Rekommendationer</h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    {results.overall >= 80 && (
                      <p>🎉 Fantastiskt! Ni har en stark Okej-kultur. Fokusera på att bibehålla och sprida denna kultur.</p>
                    )}
                    {results.overall >= 60 && results.overall < 80 && (
                      <p>👍 Bra grund! Identifiera de områden med lägst poäng och skapa handlingsplaner för förbättring.</p>
                    )}
                    {results.overall >= 40 && results.overall < 60 && (
                      <p>⚡ Utvecklingspotential! Börja med att implementera grundläggande Okej-kultur principer i era team.</p>
                    )}
                    {results.overall < 40 && (
                      <p>🚀 Stor förbättringspotential! Överväg en strukturerad kulturförändring med fokus på psykologisk trygghet.</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                  <button 
                    onClick={restartAssessment}
                    className="flex items-center justify-center px-6 py-3 border-2 border-blue-600 text-blue-600 font-semibold rounded-full hover:bg-blue-50 transition-all duration-200"
                  >
                    <SafeIcon icon={FiRefreshCw} className="mr-2" />
                    Gör om utvärdering
                  </button>
                  <Link 
                    to="/resurser" 
                    className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    Se resurser för förbättring
                    <SafeIcon icon={FiArrowRight} className="ml-2" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{color: 'rgb(17,24,39)'}} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Okej-<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">utvärdering</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Utvärdera din organisations nuvarande kultur och upptäck var ni står idag jämfört med en fullt utvecklad Okej-kultur.
            </p>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">Framsteg</span>
                <span className="text-sm text-gray-600">{currentSection + 1} av {sections.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 bg-gradient-to-r from-blue-600 to-green-600 rounded-full transition-all duration-300"
                  style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
                ></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Assessment Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSection}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <div className="text-center mb-8">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${sections[currentSection].color} flex items-center justify-center mx-auto mb-4`}>
                    <SafeIcon icon={sections[currentSection].icon} className="text-white text-3xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {sections[currentSection].title}
                  </h2>
                  <p className="text-gray-600">
                    Bedöm följande påståenden baserat på hur väl de stämmer för din organisation
                  </p>
                </div>
                <div className="space-y-8">
                  {sections[currentSection].questions.map((question, index) => (
                    <div key={question.id} className="border-b border-gray-100 pb-6 last:border-b-0">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        {question.question}
                      </h3>
                      <div className="grid grid-cols-5 gap-2">
                        {scaleLabels.map((label, scaleIndex) => (
                          <label key={scaleIndex} className="flex flex-col items-center cursor-pointer">
                            <input
                              type="radio"
                              name={question.id}
                              value={scaleIndex}
                              checked={answers[question.id] === scaleIndex}
                              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                              className="mb-2 w-4 h-4 text-blue-600"
                            />
                            <span className="text-xs text-gray-600 text-center leading-tight">
                              {label}
                            </span>
                            <span className="text-xs text-gray-400 mt-1">
                              {scaleIndex}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                  <button
                    onClick={prevSection}
                    disabled={currentSection === 0}
                    className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                      currentSection === 0 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Föregående
                  </button>
                  <button
                    onClick={nextSection}
                    disabled={!isCurrentSectionComplete()}
                    className={`flex items-center px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                      !isCurrentSectionComplete()
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-green-600 text-white hover:shadow-lg transform hover:scale-105'
                    }`}
                  >
                    {currentSection === sections.length - 1 ? 'Se resultat' : 'Nästa'}
                    <SafeIcon icon={currentSection === sections.length - 1 ? FiBarChart : FiArrowRight} className="ml-2" />
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}

export default Assessment;